/**
 * Apartment Lease Creator — 5-Step Wizard (Feb 2026 rev 3)
 *
 * Step 1: Template   — pick Classic / Executive / Modern
 * Step 2: Parties    — landlord + property + tenant (all required)
 * Step 3: Terms      — dates + rent + rules (required rent/dates)
 * Step 4: Sign+Init  — cursive/drawn signatures + INITIALS on every page
 * Step 5: Review     — preview, download, or email the signed PDF
 *
 * Every "Next" button is DISABLED until required fields on that step
 * are filled — no way to reach the final PDF with blanks.  Users can
 * backdate any date freely (no date restriction).
 */
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, FileDown, Key, Home as HomeIcon, DollarSign, Calendar,
  Users, ShieldCheck, PenLine, Zap, Check, Eraser, Type, Mail, Eye, X, Send,
  FolderOpen, Save, Trash2, RefreshCw, Edit3, Copy, Inbox, AlertTriangle,
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import DateRoller from "../components/DateRoller";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Format a typed signature name so only the FIRST letter of each word is
 * capital and everything else is lowercase.  Matches what the backend PDF
 * generator does in `_signature_case`, so the front-end preview and the
 * final PDF stay in sync.
 */
function toSignatureCase(s) {
  return (s || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

const SIGNATURE_FONTS = [
  { id: "GreatVibes", label: "Great Vibes", family: "'Great Vibes', cursive" },
  { id: "DancingScript", label: "Dancing Script", family: "'Dancing Script', cursive" },
  { id: "Allura", label: "Allura", family: "'Allura', cursive" },
];

const TEMPLATES = [
  { id: "classic", name: "Classic Legal", desc: "Formal Garamond serif · blue letterhead rule · traditional attorney look.", accent: "from-blue-600 to-indigo-800", swatch: "bg-gradient-to-br from-slate-100 to-blue-50 border-blue-800" },
  { id: "executive", name: "Executive", desc: "Burgundy + gold monogram header · decorative rules · high-end.", accent: "from-red-900 to-amber-700", swatch: "bg-gradient-to-br from-yellow-50 to-red-50 border-red-900" },
  { id: "modern", name: "Modern Minimalist", desc: "Cyan accent bar · clean sans-serif · contemporary property brand.", accent: "from-sky-500 to-cyan-600", swatch: "bg-gradient-to-br from-slate-50 to-cyan-50 border-cyan-600" },
];

const initialForm = {
  company_name: "", landlord_name: "", landlord_address: "",
  landlord_address_city: "", landlord_address_state: "", landlord_address_zip: "",
  landlord_phone: "", landlord_email: "",
  property_address: "", property_unit: "", property_city: "", property_state: "", property_zip: "",
  tenant_names: "", tenant_phone: "", tenant_email: "",
  // Optional tenant address (for identification only)
  tenant_address: "", tenant_city: "", tenant_state: "", tenant_zip: "",
  term_type: "fixed", start_date: "", end_date: "", move_in_date: "",
  monthly_rent: "", security_deposit: "", late_fee_amount: "50", grace_days: "5", nsf_fee: "35",
  // NEW — section include/exclude toggles.  When include_utilities_section
  // is false, the whole "Utilities & House Rules" block (utilities line,
  // pets, smoking) is left out of the generated PDF.  When
  // include_additional_terms is false, that whole section vanishes too.
  include_utilities_section: true,
  utilities_included: "", pets_allowed: false, pet_deposit: "",
  smoking_allowed: false, max_occupants: "",
  include_additional_terms: true,
  additional_terms: "",
  signature_font: "GreatVibes",
  landlord_signature_name: "", tenant_signature_name: "",
  landlord_signature_image: "", tenant_signature_image: "",
  landlord_sig_mode: "cursive", tenant_sig_mode: "cursive",
  // NEW — electronic initials that appear on EVERY page footer.
  landlord_initials_text: "", tenant_initials_text: "",
  landlord_initials_image: "", tenant_initials_image: "",
  landlord_ini_mode: "type", tenant_ini_mode: "type",
  template: "classic",
  // Feb 2026 — user-editable header/date overrides.  Leave blank to
  // use the defaults (today's date, "Residential Lease Agreement",
  // start_date as effective date, today for signature dates).
  prepared_date: "", prepared_header: "",
  effective_date: "",
  landlord_signature_date: "", tenant_signature_date: "",
};

// Preset phrases for "Additional Terms" dropdown — user can pick one, edit,
// or type freeform.  Keeping the list short means the dropdown feels curated.
const ADDITIONAL_TERMS_PRESETS = [
  "All utilities must remain active and current for the entire lease term.",
  "Quiet hours are in effect from 10:00 PM to 7:00 AM every day.",
  "Trash pickup is on Monday morning — bins out by 7:00 AM, back in by dusk.",
  "No overnight guests for more than 7 consecutive nights without written landlord approval.",
  "Any modifications, painting, or fixtures require written landlord approval.",
  "Tenant is responsible for lawn care and general upkeep of the exterior.",
  "Tenant must maintain renter's insurance for the full term of the lease.",
  "Smoking of any kind is strictly prohibited inside the residence.",
];

// Required-field validators per step
const stepValidators = {
  1: (f) => f.template ? [] : ["Pick a template"],
  2: (f) => {
    const missing = [];
    if (!f.company_name.trim()) missing.push("Company / Property Name");
    if (!f.landlord_name.trim()) missing.push("Landlord Name");
    if (!f.landlord_phone.trim()) missing.push("Landlord Phone");
    if (!f.landlord_email.trim()) missing.push("Landlord Email");
    if (!f.property_address.trim()) missing.push("Property Address");
    if (!f.property_city.trim()) missing.push("City");
    if (!f.property_state.trim()) missing.push("State");
    if (!f.property_zip.trim()) missing.push("ZIP");
    if (!f.tenant_names.trim()) missing.push("Tenant Name(s)");
    if (!f.tenant_phone.trim()) missing.push("Tenant Phone");
    if (!f.tenant_email.trim()) missing.push("Tenant Email");
    return missing;
  },
  3: (f) => {
    const missing = [];
    if (!f.start_date) missing.push("Start Date");
    if (f.term_type === "fixed" && !f.end_date) missing.push("End Date");
    if (!f.move_in_date) missing.push("Move-in Date");
    if (!f.monthly_rent) missing.push("Monthly Rent");
    if (!f.security_deposit) missing.push("Security Deposit");
    if (!f.late_fee_amount) missing.push("Late Fee");
    if (!f.grace_days) missing.push("Grace Days");
    return missing;
  },
  4: (f) => {
    const missing = [];
    const okLandlord = f.landlord_sig_mode === "blank"
      ? true
      : f.landlord_sig_mode === "draw"
        ? !!f.landlord_signature_image
        : !!(f.landlord_signature_name || f.landlord_name);
    const okTenant = f.tenant_sig_mode === "blank"
      ? true
      : f.tenant_sig_mode === "draw"
        ? !!f.tenant_signature_image
        : !!(f.tenant_signature_name || f.tenant_names);
    if (!okLandlord) missing.push("Landlord Signature");
    if (!okTenant) missing.push("Tenant Signature");
    // Initials — required so they stamp on every page footer (unless blank).
    const okLLInit = f.landlord_ini_mode === "blank"
      ? true
      : f.landlord_ini_mode === "draw"
        ? !!f.landlord_initials_image
        : !!(f.landlord_initials_text || "").trim();
    const okTNInit = f.tenant_ini_mode === "blank"
      ? true
      : f.tenant_ini_mode === "draw"
        ? !!f.tenant_initials_image
        : !!(f.tenant_initials_text || "").trim();
    if (!okLLInit) missing.push("Landlord Initials");
    if (!okTNInit) missing.push("Tenant Initials");
    return missing;
  },
  5: () => [],  // Review step — nothing new required
};

export default function ApartmentLeasePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [generating, setGenerating] = useState(false);
  const [savedLeaseId, setSavedLeaseId] = useState(null);   // when editing an existing saved lease
  const [showLibrary, setShowLibrary] = useState(false);

  // 2026-09-28 — tenant intake link + inbox
  const [intakePendingCount, setIntakePendingCount] = useState(0);
  const [showIntakeInbox, setShowIntakeInbox] = useState(false);
  const [intakeItems, setIntakeItems] = useState([]);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [previewIntake, setPreviewIntake] = useState(null); // full submission when clicked

  // Poll for pending intake submissions every 30s so the badge stays fresh.
  useEffect(() => {
    let cancel = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/api/lease-intake/pending-count`);
        if (!cancel) setIntakePendingCount(r.data.pending_count || 0);
      } catch (_e) { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Load full list when the user opens the inbox modal.
  useEffect(() => {
    if (!showIntakeInbox) return;
    (async () => {
      setIntakeLoading(true);
      try {
        const r = await axios.get(`${API}/api/lease-intake/pending`);
        setIntakeItems(r.data.items || []);
      } catch (_e) {
        toast.error("Failed to load intake submissions");
      } finally {
        setIntakeLoading(false);
      }
    })();
  }, [showIntakeInbox]);

  const openIntakeSubmission = async (id) => {
    try {
      const r = await axios.get(`${API}/api/lease-intake/${id}`);
      setPreviewIntake(r.data);
    } catch (_e) {
      toast.error("Failed to open submission");
    }
  };

  const importIntakeSubmission = async (sub) => {
    // Map every intake field into the ApartmentLease form.  Skip empty
    // strings so we don't overwrite existing values with blanks when
    // the tenant didn't know something.
    const f = sub?.form || {};
    const spread = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined && k in initialForm) {
        spread[k] = v;
      }
    });
    // Tenant sig + initials — map onto the "draw" mode fields that the
    // lease builder uses.
    if (f.tenant_signature_image) {
      spread.tenant_signature_image = f.tenant_signature_image;
      spread.tenant_sig_mode = "draw";
    }
    if (f.tenant_initials_image) {
      spread.tenant_initials_image = f.tenant_initials_image;
      spread.tenant_ini_mode = "draw";
    }
    skipNextAutosaveRef.current = true;
    setForm((prev) => ({ ...prev, ...spread }));
    setSavedLeaseId(null);   // Force a fresh save so we don't clobber
    setStep(2);
    setShowIntakeInbox(false);
    setPreviewIntake(null);
    toast.success("Intake imported into the lease — review and generate");
    // Mark on the backend so it drops off the pending list.
    try {
      await axios.post(`${API}/api/lease-intake/${sub.id}/import`);
      setIntakePendingCount((n) => Math.max(0, n - 1));
    } catch (_e) { /* not fatal */ }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const dismissIntakeSubmission = async (id) => {
    if (!window.confirm("Dismiss this intake?  You won't be able to import it later.")) return;
    try {
      await axios.post(`${API}/api/lease-intake/${id}/dismiss`);
      setIntakeItems((xs) => xs.filter((x) => x.id !== id));
      setIntakePendingCount((n) => Math.max(0, n - 1));
    } catch (_e) {
      toast.error("Failed to dismiss");
    }
  };

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const loadSavedLease = (savedForm, id) => {
    skipNextAutosaveRef.current = true;   // skip autosave from the programmatic set
    setForm({ ...initialForm, ...savedForm });
    setSavedLeaseId(id);
    setShowLibrary(false);
    setStep(2);
    setAutosaveStatus("saved");
    setLastAutosaveAt(new Date());
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Saved lease loaded — you can pick up where you left off");
  };

  const startNewLease = () => {
    skipNextAutosaveRef.current = true;
    setForm(initialForm);
    setSavedLeaseId(null);
    setShowLibrary(false);
    setStep(1);
    setAutosaveStatus("idle");
    setLastAutosaveAt(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveLease = async (silent = false) => {
    try {
      const res = await axios.post(`${API}/api/leases-saved`, {
        id: savedLeaseId, form,
        title: form.property_address || "Untitled Lease",
      });
      setSavedLeaseId(res.data.id);
      if (!silent) toast.success("Lease saved to your library ✓");
      return res.data.id;
    } catch (e) {
      // 2026-02-XX (user bug report "Update Saved Lease fails") —
      // surface the real server error (e.g. Pydantic 422 message).
      const detail = e?.response?.data?.detail || e?.message || "Failed to save lease";
      console.error("[Lease Save]", detail, e);
      toast.error(`Save failed: ${typeof detail === 'string' ? detail.slice(0, 180) : JSON.stringify(detail).slice(0, 180)}`);
      return null;
    }
  };

  // ─── Feb 2026 (user request) — Autosave every draft ──────────────
  // "Save every lease even if I forget to hit save."  We debounce
  // ~1.5s after the last edit and quietly POST to /api/leases-saved.
  // Only kicks in once the user has typed *something* meaningful
  // (property address OR any name), so a blank landing on the page
  // doesn't create empty rows.  Status is surfaced next to the "Save
  // Draft" button so the user sees "Saved · a moment ago".
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  const autosaveTimerRef = useRef(null);
  const skipNextAutosaveRef = useRef(true); // skip the very first render + programmatic loads

  const hasMeaningfulContent = (f) => {
    // 2026-02-XX (user request "auto save even on earlier steps") —
    // Trigger autosave as soon as the user has done ANYTHING: picked a
    // template, started typing a name, picked a date, etc.  Previously
    // required a landlord/tenant/property name which meant the whole
    // Step 1 template pick was never auto-saved.
    return Boolean(
      (f.property_address || "").trim() ||
      (f.landlord_name || "").trim() ||
      (f.tenant_names || "").trim() ||
      (f.company_name || "").trim() ||
      (f.landlord_email || "").trim() ||
      (f.tenant_email || "").trim() ||
      f.start_date || f.move_in_date ||
      (f.monthly_rent || "").trim() ||
      // Even a template pick (not the default "classic") counts as
      // meaningful progress worth saving.
      (f.template && f.template !== "classic"),
    );
  };

  useEffect(() => {
    // Skip the initial mount and any programmatic form reset.
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!hasMeaningfulContent(form)) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setAutosaveStatus("saving");
      try {
        const res = await axios.post(`${API}/api/leases-saved`, {
          id: savedLeaseId, form,
          title: form.property_address || "Untitled Lease",
        });
        setSavedLeaseId(res.data.id);
        setAutosaveStatus("saved");
        setLastAutosaveAt(new Date());
      } catch (_e) {
        setAutosaveStatus("error");
      }
    }, 1500);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [form]);

  const currentMissing = stepValidators[step] ? stepValidators[step](form) : [];
  const canNext = currentMissing.length === 0;

  const goNext = () => {
    if (!canNext) {
      toast.error(`Please fill: ${currentMissing.slice(0, 3).join(", ")}${currentMissing.length > 3 ? "…" : ""}`);
      return;
    }
    setStep((s) => Math.min(s + 1, 5));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildPdf = async () => {
    // Final validation across signing-required steps before submit.
    for (const s of [1, 2, 3, 4]) {
      const m = stepValidators[s](form);
      if (m.length) {
        toast.error(`Step ${s} incomplete: ${m.slice(0, 2).join(", ")}${m.length > 2 ? "…" : ""}`);
        setStep(s);
        return null;
      }
    }
    const res = await axios.post(`${API}/api/lease/generate-pdf`, form, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const downloadPdf = async () => {
    setGenerating(true);
    try {
      const blob = await buildPdf();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const tenantFirst = (form.tenant_names || "").trim().split(/\s+/)[0] || "Tenant";
      const initials = (tenantFirst[0] || "T") + ((form.landlord_name || "").trim().split(/\s+/)[0]?.[0] || "L");
      const a = document.createElement("a");
      a.href = url; a.download = `${initials.toUpperCase()} lease.pdf`; a.click();
      URL.revokeObjectURL(url);
      // Feb 2026 (per user request): every generated lease is
      // automatically saved to the library so users can reopen it
      // later and edit any field.  Silent so we don't double-toast.
      await saveLease(true);
      toast.success("Signed lease downloaded ✓ (also saved to your library)");
    } catch (e) {
      // 2026-02-XX (user bug report) — surface the real server error.
      let detail = e?.message || "Failed to generate lease PDF";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch {}
      console.error("[Lease Download]", detail, e);
      toast.error(`Download failed: ${String(detail).slice(0, 180)}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-950 to-black text-white">
      {/* Header */}
      <div className="border-b border-zinc-800/80 bg-gradient-to-r from-orange-900/25 via-zinc-950/90 to-amber-900/25 px-4 sm:px-6 py-4 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center gap-3 flex-wrap">
          <Link to="/" className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
            <Key className="w-5 h-5 text-orange-400" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Apartment &amp; Rental Lease Creator</h1>
            <p className="text-xs text-zinc-400">Step {step} of 5 · Works for apartments AND home rentals · Fill each step to unlock the next.</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {/* Feb 2026 — autosave status ping so the user can visually
                confirm every keystroke is being persisted, even without
                clicking Save Draft. */}
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md transition ${
                autosaveStatus === "saving"
                  ? "bg-blue-500/15 text-blue-300 border border-blue-400/30"
                  : autosaveStatus === "saved"
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
                    : autosaveStatus === "error"
                      ? "bg-red-500/15 text-red-300 border border-red-400/30"
                      : "opacity-0 pointer-events-none"
              }`}
              data-testid="lease-autosave-status"
            >
              {autosaveStatus === "saving" && <><RefreshCw className="w-3 h-3 animate-spin" /> Saving…</>}
              {autosaveStatus === "saved" && <><Check className="w-3 h-3" /> Auto-saved</>}
              {autosaveStatus === "error" && <><X className="w-3 h-3" /> Autosave failed</>}
            </span>
            <button onClick={() => saveLease(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold"
              title="Save current progress so you can return and edit later"
              data-testid="lease-save-draft">
              <Save className="w-3.5 h-3.5" /> Save Draft
            </button>
            <button onClick={() => setShowLibrary(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold"
              data-testid="lease-library-open">
              <FolderOpen className="w-3.5 h-3.5" /> Saved Leases
            </button>
            {/* 2026-09-28 — Public intake link + inbox for tenant submissions */}
            <button
              onClick={() => {
                const url = `${window.location.origin}/lease-intake`;
                navigator.clipboard?.writeText(url);
                toast.success("Client intake link copied — paste it into a text or email");
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-bold"
              title="Copy the public link tenants use to fill out their lease info"
              data-testid="lease-copy-intake-link"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Client Link
            </button>
            <button
              onClick={() => setShowIntakeInbox(true)}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold"
              title="Tenant-submitted intakes waiting for you to import"
              data-testid="lease-intake-inbox"
            >
              <Inbox className="w-3.5 h-3.5" /> Intake Inbox
              {intakePendingCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {intakePendingCount}
                </span>
              )}
            </button>
            {savedLeaseId && (
              <button onClick={startNewLease}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold"
                data-testid="lease-new">
                + New
              </button>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-4xl mx-auto mt-3 flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setStep(n)}
              className="flex-1 text-left cursor-pointer group"
              data-testid={`lease-step-pill-${n}`}
              title={`Jump to Step ${n}: ${["Template", "Parties", "Terms", "Sign", "Send"][n - 1]}`}
            >
              <div className={`h-1.5 rounded-full transition-all ${
                n < step ? "bg-emerald-500 group-hover:bg-emerald-400"
                : n === step ? "bg-gradient-to-r from-orange-500 to-amber-500"
                : "bg-zinc-800 group-hover:bg-zinc-700"
              }`} />
              <div className={`mt-1 text-[10px] uppercase font-bold tracking-wider ${
                n === step ? "text-orange-300" : n < step ? "text-emerald-400" : "text-zinc-600 group-hover:text-zinc-400"
              }`}>
                {n < step && <Check className="w-3 h-3 inline mr-0.5" />}
                {["Template", "Parties", "Terms", "Sign", "Send"][n - 1]}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {step === 1 && <StepTemplate form={form} setForm={setForm} />}
        {step === 2 && <StepParties form={form} set={set} setForm={setForm} />}
        {step === 3 && <StepTerms form={form} set={set} setForm={setForm} />}
        {step === 4 && <StepSignatures form={form} set={set} setForm={setForm} />}
        {step === 5 && <StepReview form={form} setForm={setForm} onDownload={downloadPdf} onSave={saveLease} savedLeaseId={savedLeaseId} generating={generating} />}

        {/* Saved leases library modal */}
        {showLibrary && (
          <SavedLeasesModal
            onClose={() => setShowLibrary(false)}
            onLoad={loadSavedLease}
          />
        )}

        {/* 2026-09-28 — Tenant intake inbox modal */}
        {showIntakeInbox && (
          <div
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => { setShowIntakeInbox(false); setPreviewIntake(null); }}
            data-testid="lease-intake-inbox-modal"
          >
            <div
              className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  <Inbox className="w-5 h-5 text-fuchsia-400" />
                  <div>
                    <div className="text-lg font-bold">Tenant Intake Inbox</div>
                    <div className="text-xs text-zinc-400">Submissions from your public /lease-intake link</div>
                  </div>
                </div>
                <button
                  onClick={() => { setShowIntakeInbox(false); setPreviewIntake(null); }}
                  className="text-zinc-400 hover:text-white"
                  data-testid="lease-intake-inbox-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                {previewIntake ? (
                  <IntakePreview
                    submission={previewIntake}
                    onBack={() => setPreviewIntake(null)}
                    onImport={() => importIntakeSubmission(previewIntake)}
                    onDismiss={() => { dismissIntakeSubmission(previewIntake.id); setPreviewIntake(null); }}
                  />
                ) : intakeLoading ? (
                  <div className="text-center text-zinc-400 py-12">Loading…</div>
                ) : intakeItems.length === 0 ? (
                  <div className="text-center text-zinc-500 py-12">
                    <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <div className="text-sm">No pending intake submissions.</div>
                    <div className="text-xs mt-1">Share the public link (top-right "Copy Client Link") to collect one.</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {intakeItems.map((it) => (
                      <button
                        key={it.id}
                        onClick={() => openIntakeSubmission(it.id)}
                        className="w-full text-left bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 hover:border-fuchsia-400/50 rounded-xl p-4 transition"
                        data-testid={`lease-intake-item-${it.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-white truncate">{it.tenant_names || "(no name)"}</div>
                            <div className="text-xs text-zinc-400 truncate">
                              {it.property_address ? `${it.property_address}, ${it.property_city} ${it.property_state}` : "(no property address)"}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                              {new Date(it.submitted_at).toLocaleString()} · {it.tenant_email}
                            </div>
                          </div>
                          {it.warnings?.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300 bg-amber-950/60 border border-amber-500/40 rounded-full px-2 py-0.5 whitespace-nowrap">
                              <AlertTriangle className="w-3 h-3" /> {it.warnings.length} warn
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Nav footer */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={goBack}
            disabled={step === 1}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-semibold"
            data-testid="lease-wizard-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {currentMissing.length > 0 && (
            <p className="text-xs text-amber-300 flex-1 sm:text-center">
              ⚠ Still needed: <strong>{currentMissing.slice(0, 3).join(", ")}</strong>
              {currentMissing.length > 3 ? ` +${currentMissing.length - 3} more` : ""}
            </p>
          )}
          {step < 5 ? (
            <button
              onClick={goNext}
              disabled={!canNext}
              className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-bold transition ${
                canNext
                  ? "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white shadow-lg shadow-orange-500/30"
                  : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
              }`}
              data-testid="lease-wizard-next"
            >
              {step === 4 ? "Review & Send" : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <span className="text-xs text-zinc-500 italic">Use the buttons above to Download or Email your signed lease</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 1: Template ───────────────────────────────────────────
function StepTemplate({ form, setForm }) {
  const [previewId, setPreviewId] = useState(null);
  return (
    <Section title="1. Choose Your Template" icon="📄">
      <p className="text-xs text-zinc-400 mb-3">Every template contains the same legal clauses — only the visual style differs. Tap &ldquo;Preview sample&rdquo; to see each style before choosing.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {TEMPLATES.map((t) => (
          <div key={t.id} role="button" tabIndex={0}
            onClick={() => setForm((f) => ({ ...f, template: t.id }))}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setForm((f) => ({ ...f, template: t.id })); } }}
            className={`text-left rounded-xl border-2 p-3 transition-all cursor-pointer ${
              form.template === t.id
                ? "border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/20"
                : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"
            }`}
            data-testid={`lease-template-${t.id}`}>
            <div className={`h-16 rounded-md mb-2 ${t.swatch} border`}>
              <div className={`h-full w-full rounded-md bg-gradient-to-r ${t.accent} opacity-15`} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-white">{t.name}</span>
              {form.template === t.id && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500 text-white font-bold">SELECTED</span>}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{t.desc}</p>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewId(t.id); }}
              className="mt-2 inline-block text-[10px] text-cyan-400 hover:text-cyan-300 underline"
              data-testid={`lease-template-preview-${t.id}`}>
              Preview sample ↗
            </button>
          </div>
        ))}
      </div>
      {previewId && (
        <TemplatePreviewModal
          previewId={previewId}
          setPreviewId={setPreviewId}
          selectedId={form.template}
          onSelect={(id) => setForm((f) => ({ ...f, template: id }))}
          onClose={() => setPreviewId(null)}
        />
      )}
    </Section>
  );
}

// ─── Template preview modal with prev/next arrows + inline "Use this" ─
function TemplatePreviewModal({ previewId, setPreviewId, selectedId, onSelect, onClose }) {
  const idx = TEMPLATES.findIndex((t) => t.id === previewId);
  const current = TEMPLATES[idx] || TEMPLATES[0];
  const prevTpl = TEMPLATES[(idx - 1 + TEMPLATES.length) % TEMPLATES.length];
  const nextTpl = TEMPLATES[(idx + 1) % TEMPLATES.length];
  const url = `${API}/api/lease/sample-pdf?template=${current.id}`;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setPreviewId(prevTpl.id);
      else if (e.key === "ArrowRight") setPreviewId(nextTpl.id);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, prevTpl.id, nextTpl.id, setPreviewId]);
  const isSelected = current.id === selectedId;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col"
      data-testid="template-preview-modal">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-zinc-900/95 border-b border-zinc-700">
        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold"
          data-testid="template-preview-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => setPreviewId(prevTpl.id)}
            className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
            title={`← ${prevTpl.name}`}
            data-testid="template-preview-prev">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-bold text-zinc-200 truncate">
            {current.name} <span className="text-zinc-500">— Sample Preview ({idx + 1}/{TEMPLATES.length})</span>
          </span>
          <button onClick={() => setPreviewId(nextTpl.id)}
            className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
            title={`${nextTpl.name} →`}
            data-testid="template-preview-next">
            <ArrowLeft className="w-4 h-4 rotate-180" />
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => { onSelect(current.id); onClose(); }}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold ${
              isSelected
                ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                : "bg-orange-500 hover:bg-orange-400 text-white"
            }`}
            data-testid="template-preview-use">
            {isSelected ? "✓ In Use" : "Use This"}
          </button>
          <button onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
            data-testid="template-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Template chips — always visible so the user can jump between styles */}
      <div className="flex items-center gap-2 overflow-x-auto px-3 py-2 bg-zinc-900/70 border-b border-zinc-800">
        {TEMPLATES.map((t) => (
          <button key={t.id} onClick={() => setPreviewId(t.id)}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
              t.id === current.id
                ? "border-cyan-400 bg-cyan-500/15 text-white"
                : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-zinc-500"
            }`}
            data-testid={`template-preview-chip-${t.id}`}>
            {t.name}
            {t.id === selectedId && <span className="ml-1 text-[10px] text-emerald-300">•</span>}
          </button>
        ))}
        <a href={url} download
          className="ml-auto shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[11px] font-bold"
          data-testid="template-preview-download">
          <FileDown className="w-3.5 h-3.5" /> Download
        </a>
      </div>
      <iframe
        // key ensures the iframe reloads when template changes so browsers
        // don't reuse the cached PDF for a different template.
        key={current.id}
        src={url}
        title={`${current.name} preview`}
        className="flex-1 w-full bg-white"
        data-testid="template-preview-iframe" />
    </div>
  );
}

// ─── Reusable inline PDF preview modal with a Back button ────────
function PdfPreviewModal({ url, title, onClose, testId = "" }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    // Lock body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col"
      data-testid={`${testId}-preview-modal`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-zinc-900/95 border-b border-zinc-700">
        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold"
          data-testid={`${testId}-preview-back`}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-xs font-bold text-zinc-300 truncate max-w-[55vw] text-center">{title}</span>
        <div className="flex items-center gap-1.5">
          <a href={url} download
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-xs font-bold"
            data-testid={`${testId}-preview-download`}>
            <FileDown className="w-3.5 h-3.5" /> Download
          </a>
          <button onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
            data-testid={`${testId}-preview-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <iframe src={url} title={title} className="flex-1 w-full bg-white"
        data-testid={`${testId}-preview-iframe`} />
    </div>
  );
}

// ─── Step 2: Parties ────────────────────────────────────────────
function StepParties({ form, set, setForm }) {
  // Helper: when an address is picked from autocomplete, auto-fill the
  // three related fields (city/state/zip) in one shot.
  const fillPropertyAddress = ({ line, city, state, zip }) => setForm((f) => ({
    ...f, property_address: line ?? f.property_address,
    property_city: city ?? f.property_city,
    property_state: (state ?? f.property_state).toUpperCase().slice(0, 2),
    property_zip: (zip ?? f.property_zip).replace(/\D/g, "").slice(0, 5),
  }));
  const fillLandlordAddress = ({ line, city, state, zip }) => setForm((f) => {
    // Landlord address is a single line historically — pack city/state/zip
    // into it so the PDF layout doesn't change.
    const tail = [city, state, zip].filter(Boolean).join(", ").trim();
    return { ...f, landlord_address: tail ? `${line}, ${tail}` : line };
  });
  const fillTenantAddress = ({ line, city, state, zip }) => setForm((f) => ({
    ...f, tenant_address: line ?? f.tenant_address,
    tenant_city: city ?? f.tenant_city,
    tenant_state: (state ?? f.tenant_state).toUpperCase().slice(0, 2),
    tenant_zip: (zip ?? f.tenant_zip).replace(/\D/g, "").slice(0, 5),
  }));

  return (
    <>
      <Section title="Landlord / Company" icon={<ShieldCheck className="w-4 h-4 text-orange-400" />}>
        <Grid>
          <Field label="Company / Property Name" value={form.company_name} onChange={set("company_name")} placeholder="Sunrise Apartments LLC" required testId="lease-company" />
          <Field label="Landlord Name" value={form.landlord_name} onChange={set("landlord_name")} placeholder="Your legal name" required testId="lease-landlord-name" />
          <div className="sm:col-span-2">
            <AddressAutocomplete
              label="Landlord Address"
              value={form.landlord_address}
              onChange={set("landlord_address")}
              onSelect={fillLandlordAddress}
              testId="lease-landlord-address"
              placeholder="Start typing your address…" />
          </div>
          <Field label="Landlord Phone" value={form.landlord_phone} onChange={set("landlord_phone")} placeholder="(555) 123-4567" required testId="lease-landlord-phone" />
          <Field label="Landlord Email" value={form.landlord_email} onChange={set("landlord_email")} placeholder="you@example.com" required testId="lease-landlord-email" />
        </Grid>
      </Section>
      <Section title="Property (Apartment or Home)" icon={<HomeIcon className="w-4 h-4 text-cyan-400" />}>
        <Grid>
          <div className="sm:col-span-2">
            <AddressAutocomplete
              label="Property Street Address *"
              value={form.property_address}
              onChange={set("property_address")}
              onSelect={fillPropertyAddress}
              testId="lease-prop-address"
              placeholder="742 Evergreen Terrace" />
          </div>
          <Field label="Unit / Apt # (optional for houses)" value={form.property_unit} onChange={set("property_unit")} placeholder="Apt 4B — leave blank for houses" testId="lease-prop-unit" />
          <Field label="City" value={form.property_city} onChange={set("property_city")} placeholder="Springfield" required testId="lease-prop-city" />
          <Field label="State" value={form.property_state} onChange={set("property_state")} placeholder="CA" required testId="lease-prop-state" />
          <Field label="ZIP" value={form.property_zip} onChange={set("property_zip")} placeholder="90210" required testId="lease-prop-zip" />
          <Field label="Max Occupants" value={form.max_occupants} onChange={set("max_occupants")} placeholder="4" type="number" testId="lease-max-occ" />
        </Grid>
      </Section>
      <Section title="Tenant(s)" icon={<Users className="w-4 h-4 text-violet-400" />}>
        <Grid>
          <Field label="Tenant Name(s)" value={form.tenant_names} onChange={set("tenant_names")} placeholder="Jane Doe, John Doe" required span={2} testId="lease-tenant-names" />
          <div className="sm:col-span-2">
            <AddressAutocomplete
              label="Tenant Current Address (optional)"
              value={form.tenant_address}
              onChange={set("tenant_address")}
              onSelect={fillTenantAddress}
              testId="lease-tenant-address"
              placeholder="For identification only" />
          </div>
          <Field label="Tenant Phone" value={form.tenant_phone} onChange={set("tenant_phone")} placeholder="(555) 987-6543" required testId="lease-tenant-phone" />
          <Field label="Tenant Email" value={form.tenant_email} onChange={set("tenant_email")} placeholder="tenant@email.com" required testId="lease-tenant-email" />
        </Grid>
      </Section>
    </>
  );
}

// ─── Step 3: Terms ──────────────────────────────────────────────
function StepTerms({ form, set, setForm }) {
  const toggleSection = (key) => setForm((f) => ({ ...f, [key]: !f[key] }));
  return (
    <>
      <Section title="Lease Term" icon={<Calendar className="w-4 h-4 text-teal-400" />}>
        <p className="text-[11px] text-zinc-500 mb-2">💡 You can backdate to <em>any</em> date — no restrictions.</p>
        <div className="flex gap-2 mb-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="radio" value="fixed" checked={form.term_type === "fixed"} onChange={set("term_type")} className="accent-orange-500" data-testid="lease-term-fixed" />
            Fixed Term
          </label>
          <label className="inline-flex items-center gap-2 text-sm ml-4">
            <input type="radio" value="month_to_month" checked={form.term_type === "month_to_month"} onChange={set("term_type")} className="accent-orange-500" data-testid="lease-term-mtm" />
            Month-to-Month
          </label>
        </div>
        <div className="space-y-3">
          <DateRollerField
            label="Start Date *"
            value={form.start_date}
            onChange={(iso) => setForm((f) => ({ ...f, start_date: iso }))}
            testId="lease-start-date"
          />
          {form.term_type === "fixed" && (
            <DateRollerField
              label="End Date *"
              value={form.end_date}
              onChange={(iso) => setForm((f) => ({ ...f, end_date: iso }))}
              testId="lease-end-date"
            />
          )}
          <DateRollerField
            label="Move-in Date *"
            value={form.move_in_date}
            onChange={(iso) => setForm((f) => ({ ...f, move_in_date: iso }))}
            testId="lease-movein-date"
          />

          {/* ── Feb 2026 (user request) — Optional date & header overrides ──
              These sit right underneath the main dates so they're easy to
              find on every generated / reloaded lease.  All optional. */}
          <div className="mt-4 pt-4 border-t border-dashed border-zinc-700 space-y-3">
            <div className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-fuchsia-400" />
              <span className="text-[12px] uppercase tracking-wider font-bold text-fuchsia-300">
                Header &amp; Signature Dates — Optional Overrides
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 -mt-1">
              Leave blank to use the smart defaults (today for prepared/signed,
              start date for effective, &quot;Residential Lease Agreement&quot;
              for the top-right label).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Prepared Date (top of every page)</Label>
                <input type="date" value={form.prepared_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, prepared_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 focus:border-fuchsia-500 outline-none text-white text-sm"
                  data-testid="lease-prepared-date" />
              </div>
              <div>
                <Label>Prepared Header (top-right label)</Label>
                <input type="text" value={form.prepared_header || ""}
                  onChange={(e) => setForm((f) => ({ ...f, prepared_header: e.target.value }))}
                  placeholder="Residential Lease Agreement"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 focus:border-fuchsia-500 outline-none text-white text-sm"
                  data-testid="lease-prepared-header" />
              </div>
              <div className="sm:col-span-2">
                <Label>Effective Date (under document title)</Label>
                <input type="date" value={form.effective_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 focus:border-fuchsia-500 outline-none text-white text-sm"
                  data-testid="lease-effective-date" />
                <p className="text-[11px] text-zinc-500 mt-1">
                  Defaults to <b>{form.start_date || "today"}</b> if blank.
                </p>
              </div>
              <div>
                <Label>Landlord Signature Date</Label>
                <input type="date" value={form.landlord_signature_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, landlord_signature_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 focus:border-fuchsia-500 outline-none text-white text-sm"
                  data-testid="lease-landlord-sig-date" />
              </div>
              <div>
                <Label>Tenant Signature Date</Label>
                <input type="date" value={form.tenant_signature_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, tenant_signature_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 focus:border-fuchsia-500 outline-none text-white text-sm"
                  data-testid="lease-tenant-sig-date" />
              </div>
            </div>
          </div>
        </div>
      </Section>
      <Section title="Rent & Fees" icon={<DollarSign className="w-4 h-4 text-emerald-400" />}>
        <Grid>
          <Field label="Monthly Rent ($)" value={form.monthly_rent} onChange={set("monthly_rent")} placeholder="2500" type="number" required testId="lease-rent" />
          <Field label="Security Deposit ($)" value={form.security_deposit} onChange={set("security_deposit")} placeholder="2500" type="number" required testId="lease-deposit" />
          <Field label="Late Fee ($)" value={form.late_fee_amount} onChange={set("late_fee_amount")} type="number" required testId="lease-latefee" />
          <Field label="Grace Days" value={form.grace_days} onChange={set("grace_days")} type="number" required testId="lease-grace" />
          <Field label="NSF Fee ($)" value={form.nsf_fee} onChange={set("nsf_fee")} type="number" testId="lease-nsf" />
        </Grid>
      </Section>

      {/* ─── Utilities & House Rules — optional ────────────── */}
      <div className={`rounded-xl border p-4 transition ${
          form.include_utilities_section
            ? "border-zinc-700 bg-zinc-900/60"
            : "border-zinc-800/70 bg-zinc-950/40 opacity-70"
        }`}>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            <h3 className="text-sm font-bold text-white">Utilities & House Rules</h3>
            {!form.include_utilities_section && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-200 font-bold uppercase">
                Excluded from PDF
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => toggleSection("include_utilities_section")}
            className={`text-xs font-bold px-2.5 py-1 rounded-lg transition ${
              form.include_utilities_section
                ? "bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
                : "bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30"
            }`}
            data-testid="lease-toggle-utilities-section"
          >
            {form.include_utilities_section ? "Remove this section" : "+ Include this section"}
          </button>
        </div>
        {form.include_utilities_section && (
          <>
            <Grid>
              <Field label="Utilities Included (comma-separated)" value={form.utilities_included} onChange={set("utilities_included")} placeholder="Water, Trash, Gas" span={2} testId="lease-utilities" />
            </Grid>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <label className="flex items-center gap-2 text-sm text-zinc-300 p-3 rounded-lg border border-zinc-700 bg-zinc-900/60">
                <input type="checkbox" checked={form.pets_allowed} onChange={set("pets_allowed")} className="accent-orange-500 w-4 h-4" data-testid="lease-pets" />
                <span>Pets Allowed</span>
              </label>
              {form.pets_allowed && <Field label="Pet Deposit ($)" value={form.pet_deposit} onChange={set("pet_deposit")} type="number" placeholder="250" testId="lease-pet-dep" />}
              <label className="flex items-center gap-2 text-sm text-zinc-300 p-3 rounded-lg border border-zinc-700 bg-zinc-900/60">
                <input type="checkbox" checked={form.smoking_allowed} onChange={set("smoking_allowed")} className="accent-orange-500 w-4 h-4" data-testid="lease-smoking" />
                <span>Smoking Allowed (designated areas)</span>
              </label>
            </div>
          </>
        )}
      </div>

      {/* ─── Additional Terms — optional, with preset dropdown ── */}
      <div className={`rounded-xl border p-4 transition ${
          form.include_additional_terms
            ? "border-zinc-700 bg-zinc-900/60"
            : "border-zinc-800/70 bg-zinc-950/40 opacity-70"
        }`}>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-sky-400" />
            <h3 className="text-sm font-bold text-white">Additional Terms</h3>
            {!form.include_additional_terms && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-200 font-bold uppercase">
                Excluded from PDF
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => toggleSection("include_additional_terms")}
            className={`text-xs font-bold px-2.5 py-1 rounded-lg transition ${
              form.include_additional_terms
                ? "bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
                : "bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30"
            }`}
            data-testid="lease-toggle-additional-section"
          >
            {form.include_additional_terms ? "Remove this section" : "+ Include this section"}
          </button>
        </div>
        {form.include_additional_terms && (
          <>
            {/* Preset dropdown — picking a preset appends it to the textarea. */}
            <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
              Quick-Insert Preset (optional)
            </label>
            <select
              value=""
              onChange={(e) => {
                const preset = e.target.value;
                if (!preset) return;
                setForm((f) => ({
                  ...f,
                  additional_terms: f.additional_terms
                    ? `${f.additional_terms.trim()}\n${preset}`
                    : preset,
                }));
                e.target.value = "";  // reset so the same preset can be picked again
              }}
              className="w-full mt-1 mb-2 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
              data-testid="lease-additional-preset"
            >
              <option value="">— Pick a common clause to insert —</option>
              {ADDITIONAL_TERMS_PRESETS.map((p, i) => (
                <option key={i} value={p}>{p}</option>
              ))}
            </select>
            <textarea rows={4} value={form.additional_terms} onChange={set("additional_terms")}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              placeholder="Type your own clauses here, or pick from the dropdown above." data-testid="lease-additional" />
          </>
        )}
      </div>
    </>
  );
}

// ─── Step 4: Signatures ─────────────────────────────────────────
function StepSignatures({ form, set, setForm }) {
  const selectedFont = SIGNATURE_FONTS.find((f) => f.id === form.signature_font) || SIGNATURE_FONTS[0];
  return (
    <>
      <Section title="Signature Style (Cursive Font)" icon={<Type className="w-4 h-4 text-fuchsia-400" />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {SIGNATURE_FONTS.map((f) => (
            <button key={f.id} type="button" onClick={() => setForm((x) => ({ ...x, signature_font: f.id }))}
              className={`rounded-lg border-2 p-3 text-center transition ${
                form.signature_font === f.id
                  ? "border-fuchsia-500 bg-fuchsia-500/10"
                  : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"
              }`}
              data-testid={`lease-font-${f.id}`}>
              <div className="text-xl text-orange-200" style={{ fontFamily: f.family }}>Signature</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1 font-bold">{f.label}</div>
            </button>
          ))}
        </div>
      </Section>

      <SignaturePicker
        title="Landlord Signature"
        mode={form.landlord_sig_mode}
        setMode={(m) => setForm((x) => ({
          ...x, landlord_sig_mode: m,
          ...(m === "blank" ? { landlord_signature_name: "", landlord_signature_image: "" } : {}),
        }))}
        cursiveName={form.landlord_signature_name}
        onCursiveChange={set("landlord_signature_name")}
        fallbackName={form.landlord_name}
        imageDataUri={form.landlord_signature_image}
        onImageChange={(img) => setForm((x) => ({ ...x, landlord_signature_image: img }))}
        selectedFont={selectedFont}
        role="landlord"
      />
      <InitialsPicker
        title="Landlord Initials (stamped on every page)"
        mode={form.landlord_ini_mode}
        setMode={(m) => setForm((x) => ({
          ...x, landlord_ini_mode: m,
          ...(m === "blank" ? { landlord_initials_text: "", landlord_initials_image: "" } : {}),
        }))}
        typedText={form.landlord_initials_text}
        onTypedChange={set("landlord_initials_text")}
        imageDataUri={form.landlord_initials_image}
        onImageChange={(img) => setForm((x) => ({ ...x, landlord_initials_image: img }))}
        role="landlord-initials"
        suggestion={initialsFrom(form.landlord_name)}
      />
      <SignaturePicker
        title="Tenant Signature"
        mode={form.tenant_sig_mode}
        setMode={(m) => setForm((x) => ({
          ...x, tenant_sig_mode: m,
          ...(m === "blank" ? { tenant_signature_name: "", tenant_signature_image: "" } : {}),
        }))}
        cursiveName={form.tenant_signature_name}
        onCursiveChange={set("tenant_signature_name")}
        fallbackName={form.tenant_names}
        imageDataUri={form.tenant_signature_image}
        onImageChange={(img) => setForm((x) => ({ ...x, tenant_signature_image: img }))}
        selectedFont={selectedFont}
        role="tenant"
      />
      <InitialsPicker
        title="Tenant Initials (stamped on every page)"
        mode={form.tenant_ini_mode}
        setMode={(m) => setForm((x) => ({
          ...x, tenant_ini_mode: m,
          ...(m === "blank" ? { tenant_initials_text: "", tenant_initials_image: "" } : {}),
        }))}
        typedText={form.tenant_initials_text}
        onTypedChange={set("tenant_initials_text")}
        imageDataUri={form.tenant_initials_image}
        onImageChange={(img) => setForm((x) => ({ ...x, tenant_initials_image: img }))}
        role="tenant-initials"
        suggestion={initialsFrom(form.tenant_names)}
      />
    </>
  );
}

// Auto-derive two-letter initials from a full name string.
function initialsFrom(fullName) {
  if (!fullName) return "";
  const parts = String(fullName).trim().split(/[\s&,]+/).filter(Boolean);
  const first = parts[0] || "";
  const last = parts[parts.length - 1] || "";
  return ((first[0] || "") + (last[0] || first[1] || "")).toUpperCase();
}

// ─── Initials picker (typed 2-4 chars, drawn, or leave blank) ────
function InitialsPicker({ title, mode, setMode, typedText, onTypedChange, imageDataUri, onImageChange, role, suggestion }) {
  return (
    <Section title={title} icon={<PenLine className="w-4 h-4 text-emerald-400" />}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <button type="button" onClick={() => setMode("type")}
          className={`text-left rounded-lg border-2 p-2.5 transition ${mode === "type" ? "border-emerald-500 bg-emerald-500/15" : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"}`}
          data-testid={`lease-${role}-mode-type`}>
          <div className="text-xs font-bold text-white uppercase tracking-wider">Type Initials</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Fastest — 2-4 letters like MT.</div>
        </button>
        <button type="button" onClick={() => setMode("draw")}
          className={`text-left rounded-lg border-2 p-2.5 transition ${mode === "draw" ? "border-emerald-500 bg-emerald-500/15" : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"}`}
          data-testid={`lease-${role}-mode-draw`}>
          <div className="text-xs font-bold text-white uppercase tracking-wider">Draw Initials</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Handwritten with mouse or finger.</div>
        </button>
        <button type="button" onClick={() => setMode("blank")}
          className={`text-left rounded-lg border-2 p-2.5 transition ${mode === "blank" ? "border-emerald-500 bg-emerald-500/15" : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"}`}
          data-testid={`lease-${role}-mode-blank`}>
          <div className="text-xs font-bold text-white uppercase tracking-wider">Leave Blank</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Sign by hand on every page later.</div>
        </button>
      </div>
      {mode === "type" && (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={typedText} onChange={onTypedChange} maxLength={4}
              placeholder="e.g. MT"
              className="w-40 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-lg font-bold uppercase tracking-widest text-center"
              data-testid={`lease-${role}-typed`} />
            {suggestion && !typedText && (
              <button type="button" onClick={() => onTypedChange({ target: { value: suggestion } })}
                className="text-[11px] px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 font-bold"
                data-testid={`lease-${role}-use-suggestion`}>
                Use &ldquo;{suggestion}&rdquo;
              </button>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">These initials will be stamped on every page footer.</p>
        </div>
      )}
      {mode === "draw" && (
        <SignatureCanvas dataUri={imageDataUri} onChange={onImageChange} role={role} height={120} />
      )}
      {mode === "blank" && (
        <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
          <p className="text-sm font-bold text-amber-300 mb-1">Blank initials line</p>
          <p className="text-[11px] text-zinc-400">
            Every page footer will show an empty underscore where you initial by hand after printing.
          </p>
        </div>
      )}
    </Section>
  );
}

// ─── Step 5: Review, Download, Email ───────────────────────────
function StepReview({ form, setForm, onDownload, onSave, savedLeaseId, generating }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Auto-seed recipients with landlord + tenant emails
  useEffect(() => {
    const seeds = [form.landlord_email, form.tenant_email].filter((e) => e && e.includes("@"));
    setRecipients((prev) => (prev.length ? prev : Array.from(new Set(seeds))));
  }, [form.landlord_email, form.tenant_email]);

  // 2026-02-08 user request — on the LAST step (review), pre-fill the
  // Effective Date + both Signature Dates with the move-in date if they
  // haven't been set explicitly.  User can still tweak any of the three
  // right before hitting Download.
  useEffect(() => {
    const mi = form.move_in_date;
    if (!mi) return;
    setForm((f) => {
      const patch = {};
      if (!f.effective_date) patch.effective_date = mi;
      if (!f.landlord_signature_date) patch.landlord_signature_date = mi;
      if (!f.tenant_signature_date) patch.tenant_signature_date = mi;
      return Object.keys(patch).length ? { ...f, ...patch } : f;
    });
  }, [form.move_in_date, setForm]);

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/api/lease/generate-pdf`, form, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch (e) {
      // 2026-02-XX (user bug report "unable to preview/download/update") —
      // surface the ACTUAL server error instead of a generic toast so if
      // the backend throws a validation / render error the user can see
      // exactly what field tripped it.  Blob error responses need to be
      // read as text first.
      let detail = e?.message || "Failed to build preview";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch {}
      console.error("[Lease Preview]", detail, e);
      toast.error(`Preview failed: ${String(detail).slice(0, 180)}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const addRecipient = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (!t.includes("@") || !t.includes(".")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (recipients.includes(t)) return;
    setRecipients((r) => [...r, t]);
    setEmailDraft("");
  };

  const removeRecipient = (e) => setRecipients((r) => r.filter((x) => x !== e));

  const sendEmail = async () => {
    if (!recipients.length) {
      toast.error("Add at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, recipients, subject, message };
      const res = await axios.post(`${API}/api/lease/email-pdf`, payload);
      const { sent = [], failed = [] } = res.data || {};
      if (sent.length) toast.success(`Signed lease emailed to ${sent.length} recipient${sent.length > 1 ? "s" : ""} ✓`);
      if (failed.length) toast.error(`Failed for ${failed.length}: ${failed.map((f) => f.email).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* 2026-02-08 user request — final date-check panel right before
          the user hits Download. Pre-filled from Move-in date; all
          three fields still editable so a signing date that differs
          from the move-in day is easy to set at the last minute. */}
      <Section title="Confirm Dates — Last Chance Before Download" icon={<Calendar className="w-4 h-4 text-fuchsia-400" />}>
        <p className="text-[11px] text-zinc-500 mb-3">
          These dates print on <em>every page header</em> and above the signature lines on the last page. Defaults to your <b>Move-in Date</b> ({form.move_in_date || "not set"}) — tweak any of them below if the signing day differs.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Lease Date (top of every page)</Label>
            <input
              type="date"
              value={form.effective_date || ""}
              onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-fuchsia-700/40 focus:border-fuchsia-500 outline-none text-white text-sm"
              data-testid="review-effective-date"
            />
          </div>
          <div>
            <Label>Landlord Signature Date</Label>
            <input
              type="date"
              value={form.landlord_signature_date || ""}
              onChange={(e) => setForm((f) => ({ ...f, landlord_signature_date: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-fuchsia-700/40 focus:border-fuchsia-500 outline-none text-white text-sm"
              data-testid="review-landlord-sig-date"
            />
          </div>
          <div>
            <Label>Tenant Signature Date</Label>
            <input
              type="date"
              value={form.tenant_signature_date || ""}
              onChange={(e) => setForm((f) => ({ ...f, tenant_signature_date: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-fuchsia-700/40 focus:border-fuchsia-500 outline-none text-white text-sm"
              data-testid="review-tenant-sig-date"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            const mi = form.move_in_date;
            if (!mi) { toast.error("Move-in date is not set"); return; }
            setForm((f) => ({ ...f, effective_date: mi, landlord_signature_date: mi, tenant_signature_date: mi }));
            toast.success("All dates reset to move-in date");
          }}
          className="mt-3 text-[11px] px-3 py-1.5 rounded-full border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
          data-testid="review-reset-dates-btn"
        >
          ↺ Reset all to move-in date
        </button>
      </Section>

      <Section title="Review — Preview Your Signed Lease" icon={<Eye className="w-4 h-4 text-cyan-400" />}>
        <p className="text-[11px] text-zinc-500 mb-3">
          Tap <em>Preview</em> to render the full PDF with all signatures and initials.
          You can preview as many times as you need before sending.
        </p>
        <div className="flex flex-wrap gap-2">
          <button onClick={openPreview} disabled={previewLoading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-bold disabled:opacity-50"
            data-testid="lease-preview-btn">
            <Eye className="w-4 h-4" /> {previewLoading ? "Building…" : "Preview PDF"}
          </button>
          <button onClick={onDownload} disabled={generating}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-sm font-bold shadow-lg shadow-emerald-500/30 disabled:opacity-50"
            data-testid="lease-download-btn">
            <FileDown className="w-4 h-4" /> {generating ? "Downloading…" : "Download Signed PDF"}
          </button>
          <button onClick={async () => { setSaving(true); const id = await onSave(); if (id) setSavedAt(new Date().toLocaleTimeString()); setSaving(false); }}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-400 hover:to-purple-400 text-white text-sm font-bold shadow-lg shadow-fuchsia-500/30 disabled:opacity-50"
            data-testid="lease-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : (savedLeaseId ? "Update Saved Lease" : "Save to Library")}
          </button>
          {savedAt && <span className="text-[11px] text-emerald-400 self-center">✓ Saved at {savedAt}</span>}
        </div>
        {previewUrl && (
          <div className="mt-4 rounded-lg overflow-hidden border-2 border-cyan-500/30 bg-zinc-900">
            <iframe src={previewUrl} title="Lease Preview" className="w-full h-[600px] bg-white"
              data-testid="lease-preview-iframe" />
          </div>
        )}
      </Section>

      <Section title="Email the Signed Lease" icon={<Mail className="w-4 h-4 text-orange-400" />}>
        <div className="space-y-3">
          <div>
            <Label>Recipients</Label>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-lg bg-zinc-900 border border-zinc-700 min-h-[42px]">
              {recipients.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-xs text-orange-200"
                  data-testid={`lease-recipient-${e}`}>
                  {e}
                  <button onClick={() => removeRecipient(e)} className="hover:text-white" data-testid={`lease-recipient-remove-${e}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === ",") && emailDraft) { e.preventDefault(); addRecipient(emailDraft); } }}
                placeholder={recipients.length ? "Add another email + Enter…" : "email@address.com"}
                className="flex-1 min-w-[160px] bg-transparent outline-none text-white text-sm placeholder-zinc-500"
                data-testid="lease-recipient-input" />
              {emailDraft && (
                <button onClick={() => addRecipient(emailDraft)}
                  className="text-[11px] px-2 py-0.5 rounded bg-orange-500 text-white font-bold"
                  data-testid="lease-recipient-add">Add</button>
              )}
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">Press Enter or comma to add each address. Landlord + Tenant emails are pre-filled.</p>
          </div>
          <Field label="Subject (optional)" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Your Signed Residential Lease Agreement" testId="lease-email-subject" />
          <div>
            <Label>Message (optional)</Label>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal note to the recipients (HTML supported)."
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              data-testid="lease-email-message" />
          </div>
          <button onClick={sendEmail} disabled={sending || !recipients.length}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 hover:from-orange-400 hover:via-amber-400 hover:to-orange-400 text-white text-sm font-bold shadow-lg shadow-orange-500/30 disabled:opacity-50"
            data-testid="lease-send-email-btn">
            <Send className="w-4 h-4" />
            {sending ? "Sending…" : `Send Signed Lease to ${recipients.length || 0} Recipient${recipients.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </Section>
    </>
  );
}

function SignaturePicker({ title, mode, setMode, cursiveName, onCursiveChange, fallbackName, imageDataUri, onImageChange, selectedFont, role }) {
  const modes = [
    { id: "cursive", label: "Cursive Font", desc: "Type your name — we render it in beautiful script." },
    { id: "draw", label: "Draw Signature", desc: "Use mouse or finger — smooth curve smoothing built-in." },
    { id: "blank", label: "Leave Blank", desc: "Print the lease and sign by hand later." },
  ];
  return (
    <Section title={title} icon={<PenLine className="w-4 h-4 text-fuchsia-400" />}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {modes.map((m) => (
          <button key={m.id} type="button" onClick={() => setMode(m.id)}
            className={`text-left rounded-lg border-2 p-2.5 transition ${
              mode === m.id
                ? "border-fuchsia-500 bg-fuchsia-500/15 shadow-lg shadow-fuchsia-500/10"
                : "border-zinc-700 bg-zinc-900/40 hover:border-zinc-500"
            }`}
            data-testid={`lease-${role}-sig-mode-${m.id}`}>
            <div className="text-xs font-bold text-white uppercase tracking-wider">{m.label}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">{m.desc}</div>
          </button>
        ))}
      </div>
      {mode === "cursive" && (
        <>
          <input value={cursiveName} onChange={onCursiveChange}
            placeholder={fallbackName ? `Auto-filled: ${fallbackName}` : "Type name to render in cursive"}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
            data-testid={`lease-${role}-sig-cursive`} />
          <p className="text-[10px] text-zinc-500 mt-1">
            💡 Rendered with only the first letter of each name capital — no full caps.
          </p>
          <div className="mt-2 text-3xl text-orange-200 min-h-[52px] px-3 py-3 rounded bg-zinc-950/60 border border-zinc-800"
            style={{ fontFamily: selectedFont.family }}>
            {toSignatureCase(cursiveName || fallbackName) || "\u00A0"}
          </div>
        </>
      )}
      {mode === "draw" && (
        <SignatureCanvas dataUri={imageDataUri} onChange={onImageChange} role={role}
          traceText={toSignatureCase(cursiveName || fallbackName)}
          traceFont={selectedFont.family} />
      )}
      {mode === "blank" && (
        <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
          <p className="text-sm font-bold text-amber-300 mb-1">Blank signature line</p>
          <p className="text-[11px] text-zinc-400">
            The PDF will print an empty signature line where {role.includes("landlord") ? "the Landlord" : "the Tenant"} can sign by hand after printing.
          </p>
          <div className="mt-3 mx-auto max-w-sm">
            <div className="border-b-2 border-zinc-500 h-8"></div>
            <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Sign Above</p>
          </div>
        </div>
      )}
    </Section>
  );
}

// SignatureCanvas — smooth quadratic-curve signature capture with:
//   • Retina-quality (devicePixelRatio-aware) crisp strokes
//   • Midpoint quadratic smoothing (Bezier curves through 3-point windows)
//   • Slightly variable stroke width for a natural pen feel
//   • Undo (removes the last stroke)
//   • Clear (wipes the entire pad)
//   • Larger canvas (200 px tall) for easier signing
function SignatureCanvas({ dataUri, onChange, role, height = 220, traceText = "", traceFont = "Great Vibes, cursive" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const strokesRef = useRef([]);   // array of strokes; each stroke is array of {x,y,t}
  const currentStrokeRef = useRef(null);
  const [hasStroke, setHasStroke] = useState(!!dataUri);
  const [showGuide, setShowGuide] = useState(true);

  const setupCanvas = () => {
    const cvs = canvasRef.current;
    if (!cvs) return null;
    const rect = cvs.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (cvs.width !== Math.floor(rect.width * dpr) || cvs.height !== Math.floor(rect.height * dpr)) {
      cvs.width = Math.floor(rect.width * dpr);
      cvs.height = Math.floor(rect.height * dpr);
    }
    const ctx = cvs.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0b1220";
    return { ctx, dpr, w: rect.width, h: rect.height };
  };

  const redrawAll = () => {
    const info = setupCanvas();
    if (!info) return;
    const { ctx, w, h } = info;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    // Light trace-guide behind strokes so users can trace over a nice cursive.
    if (showGuide && traceText && !hasStroke) {
      drawTraceGuide(ctx, w, h, traceText, traceFont);
    }
    for (const stroke of strokesRef.current) {
      drawSmoothStroke(ctx, stroke);
    }
  };

  const drawTraceGuide = (ctx, w, h, text, font) => {
    ctx.save();
    ctx.fillStyle = "rgba(15, 23, 42, 0.14)";  // very faint slate
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Fit the text into ~85% of the pad width, up to h*0.72 tall.
    let fontSize = Math.floor(h * 0.65);
    ctx.font = `italic ${fontSize}px "${font}", "Great Vibes", cursive`;
    let m = ctx.measureText(text);
    while (m.width > w * 0.85 && fontSize > 20) {
      fontSize -= 2;
      ctx.font = `italic ${fontSize}px "${font}", "Great Vibes", cursive`;
      m = ctx.measureText(text);
    }
    ctx.fillText(text, w / 2, h * 0.55);
    ctx.restore();
  };

  // 2026-02-XX (user request "signatures look sloppy on trackpad") —
  // Trackpad input is noisy and twitchy compared to a stylus.  We now:
  //   • Raise the min-distance capture filter from 1.2 → 2.5 px so
  //     tiny micro-jitter never adds a point.
  //   • Apply a 1-pass exponential moving-average smoothing to the
  //     final stroke before rendering so the handwritten curve has
  //     the look of a stabilized pen.
  //   • Switch final render to Catmull-Rom cubic splines so the
  //     curve passes exactly through each captured point but with
  //     visibly smoother transitions than midpoint-quadratic.
  //   • Thicker base stroke width (2.4-4.8 vs 1.0-3.6) for a
  //     ballpoint-pen feel that masks residual noise.
  const smoothPointsEMA = (pts, alpha = 0.55) => {
    if (!pts || pts.length < 2) return pts || [];
    const out = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const prev = out[out.length - 1];
      out.push({
        x: alpha * pts[i].x + (1 - alpha) * prev.x,
        y: alpha * pts[i].y + (1 - alpha) * prev.y,
        t: pts[i].t,
      });
    }
    return out;
  };

  // Midpoint quadratic-Bezier smoothing PLUS velocity-based width variance so
  // slow strokes render slightly thicker (like a real ballpoint pen).
  // 2026-02-XX — upgraded to Catmull-Rom cubic Bezier splines for a
  // visibly smoother trackpad experience.
  const drawSmoothStroke = (ctx, rawPts) => {
    const pts = smoothPointsEMA(rawPts);
    if (!pts || pts.length === 0) return;
    if (pts.length === 1) {
      const p = pts[0];
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
      ctx.fillStyle = "#0b1220";
      ctx.fill();
      return;
    }
    ctx.strokeStyle = "#0b1220";
    // Catmull-Rom → cubic Bezier conversion.  Each segment uses the
    // 4 surrounding points p[i-1], p[i], p[i+1], p[i+2] to compute
    // control points that make the curve pass through p[i] and p[i+1]
    // smoothly, with C1 continuity across segments.
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      // Velocity → width (ballpoint pen feel; slow stroke = thicker).
      const dt = Math.max(1, (p2.t || 0) - (p1.t || 0));
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const v = dist / dt;
      const width = Math.max(2.4, Math.min(4.8, 3.6 - v * 1.5));
      ctx.lineWidth = width;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, p2.x, p2.y);
      ctx.stroke();
    }
  };

  useEffect(() => {
    // First mount: load an existing PNG if provided, else set up blank pad.
    const info = setupCanvas();
    if (!info) return;
    const { ctx, w, h } = info;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    if (dataUri && strokesRef.current.length === 0) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, h);
      img.src = dataUri;
    }
    // Handle resize (window rotate / responsive width change).
    const ro = new ResizeObserver(() => redrawAll());
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top, t: performance.now() };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const p = pos(e);
    currentStrokeRef.current = [p];
    setupCanvas();
    // Wipe the trace guide on first stroke — user has committed to signing.
    if (!hasStroke) {
      redrawAll();
    }
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pos(e);
    const s = currentStrokeRef.current;
    if (!s) return;
    // Skip micro-movements to reduce jitter — raised from 1.2px → 2.5px
    // on 2026-02-XX for a dramatically smoother trackpad experience.
    const last = s[s.length - 1];
    if (Math.hypot(p.x - last.x, p.y - last.y) < 2.5) return;
    s.push(p);
    // Incrementally draw only the newest segment (variable-width smooth curve).
    const info = setupCanvas();
    if (!info) return;
    const { ctx } = info;
    if (s.length >= 3) {
      const p0 = s[s.length - 3];
      const p1 = s[s.length - 2];
      const p2 = s[s.length - 1];
      const midA = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const midB = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const dt = Math.max(1, p2.t - p1.t);
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const v = dist / dt;
      // Thicker base stroke (2.4-4.8 vs 1.0-3.6) for ballpoint-pen look.
      const width = Math.max(2.4, Math.min(4.8, 3.6 - v * 1.5));
      ctx.strokeStyle = "#0b1220";
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(midA.x, midA.y);
      ctx.quadraticCurveTo(p1.x, p1.y, midB.x, midB.y);
      ctx.stroke();
    } else if (s.length === 2) {
      ctx.strokeStyle = "#0b1220";
      ctx.lineWidth = 3.0;
      ctx.beginPath();
      ctx.moveTo(s[0].x, s[0].y);
      ctx.lineTo(s[1].x, s[1].y);
      ctx.stroke();
    }
    setHasStroke(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentStrokeRef.current && currentStrokeRef.current.length) {
      strokesRef.current.push(currentStrokeRef.current);
      // Repaint smoothly so the final stroke matches the incremental
      // preview cleanly (no visible seams).
      redrawAll();
    }
    currentStrokeRef.current = null;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const undo = () => {
    if (!strokesRef.current.length) return;
    strokesRef.current.pop();
    redrawAll();
    setHasStroke(strokesRef.current.length > 0);
    onChange(strokesRef.current.length ? canvasRef.current.toDataURL("image/png") : "");
  };

  const clear = () => {
    strokesRef.current = [];
    redrawAll();
    setHasStroke(false);
    onChange("");
  };

  return (
    <div>
      <div className="relative">
        <canvas ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ height: `${height}px`, touchAction: "none" }}
          className="w-full rounded-lg border-2 border-dashed border-fuchsia-400/50 bg-white cursor-crosshair"
          data-testid={`lease-${role}-sig-canvas`} />
        {!hasStroke && !traceText && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="text-zinc-400 text-sm italic select-none">Sign here — draw with mouse or finger</p>
          </div>
        )}
        {!hasStroke && traceText && (
          <div className="pointer-events-none absolute bottom-1 left-2 text-[10px] text-zinc-400 select-none">
            ↑ trace over the faint cursive to make a neat signature
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-[11px] text-zinc-500">Draw slowly — the smoother you go, the neater the ink.</p>
        </div>
        <div className="flex gap-1.5">
          {traceText && (
            <button type="button" onClick={() => { setShowGuide((g) => !g); redrawAll(); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
              data-testid={`lease-${role}-sig-toggle-guide`}>
              {showGuide ? "Hide" : "Show"} Guide
            </button>
          )}
          <button type="button" onClick={undo} disabled={!strokesRef.current.length}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-300"
            data-testid={`lease-${role}-sig-undo`}>
            ↶ Undo
          </button>
          <button type="button" onClick={clear}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 font-bold"
            data-testid={`lease-${role}-sig-clear`}>
            <RefreshCw className="w-3 h-3" /> Try Again
          </button>
        </div>
      </div>
      {hasStroke && <p className="text-[11px] text-emerald-400 mt-1">✓ Signature captured — tap Try Again for another go anytime</p>}
    </div>
  );
}

// ─── Shared UI helpers ─────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        {typeof icon === "string" ? <span className="text-lg">{icon}</span> : icon}
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-200">{title}</h2>
      </div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Label({ children }) { return <label className="block text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-1">{children}</label>; }
function Field({ label, value, onChange, placeholder, type = "text", required = false, span = 1, testId }) {
  const hasValue = String(value || "").trim().length > 0;
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label>{label}{required ? <span className={hasValue ? "text-emerald-400" : "text-orange-400"}> *</span> : null}</Label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500 ${
          required && !hasValue ? "border-orange-500/50" : "border-zinc-700"
        }`}
        data-testid={testId} />
    </div>
  );
}

/**
 * DateRollerField — Label + 3-column month/day/year dropdown.  Used instead
 * of `<input type="date">` because the native calendar popup is painful to
 * use for backdated leases on mobile.  Highlights the label green once a
 * complete date is picked, orange while it's still incomplete.
 */
function DateRollerField({ label, value, onChange, testId }) {
  const hasValue = String(value || "").trim().length === 10;
  return (
    <div>
      <Label>
        {label.replace(/\s\*$/, "")}
        <span className={hasValue ? "text-emerald-400" : "text-orange-400"}> *</span>
      </Label>
      <DateRoller value={value} onChange={onChange} testId={testId} />
    </div>
  );
}


// ─── Saved Leases library modal ────────────────────────────────
function SavedLeasesModal({ onClose, onLoad }) {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/leases-saved`);
      setLeases(res.data || []);
    } catch (e) {
      toast.error("Failed to load saved leases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onEsc); document.body.style.overflow = prev; };
  }, [onClose]);

  const loadOne = async (id) => {
    try {
      const res = await axios.get(`${API}/api/leases-saved/${id}`);
      onLoad(res.data.form || {}, id);
    } catch (e) {
      toast.error("Failed to load lease");
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this saved lease? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/api/leases-saved/${id}`);
      setLeases((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lease deleted");
    } catch (e) {
      toast.error("Failed to delete lease");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col"
      data-testid="lease-library-modal">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-zinc-900/95 border-b border-zinc-700">
        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold"
          data-testid="lease-library-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-sm font-bold text-white flex items-center gap-1.5">
          <FolderOpen className="w-4 h-4 text-fuchsia-400" /> Saved Leases
        </span>
        <button onClick={fetchList}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold"
          data-testid="lease-library-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 max-w-4xl w-full mx-auto">
        {loading ? (
          <div className="text-center text-zinc-400 py-8">Loading your library…</div>
        ) : leases.length === 0 ? (
          <div className="text-center text-zinc-400 py-16">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No saved leases yet.</p>
            <p className="text-xs mt-1">Finish a lease on Step 5 and tap <em>Save to Library</em> to store it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leases.map((l) => (
              <div key={l.id} className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-3 hover:border-fuchsia-500 transition"
                data-testid={`lease-saved-item-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white truncate">{l.title || l.property_address || "Untitled Lease"}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      {l.tenant_names || "—"} · ${l.monthly_rent || "?"}/mo
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Starts {l.start_date || "—"} · {l.template || "classic"}
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      Saved {l.updated_at ? new Date(l.updated_at).toLocaleString() : "—"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3">
                  <button onClick={() => loadOne(l.id)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-bold"
                    data-testid={`lease-saved-load-${l.id}`}>
                    Open
                  </button>
                  <button onClick={() => deleteOne(l.id)} disabled={deletingId === l.id}
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-bold disabled:opacity-50"
                    data-testid={`lease-saved-delete-${l.id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 2026-09-28 — Tenant intake preview.  Shows the full submission (all
// fields the tenant filled), lets you download signature/initials, and
// gives one-click "Import into Lease" + "Dismiss".
// ─────────────────────────────────────────────────────────────────────
function IntakePreview({ submission, onBack, onImport, onDismiss }) {
  const f = submission?.form || {};
  const warnings = submission?.warnings || [];
  const row = (label, val) => (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-100 text-right truncate">{val || <span className="text-zinc-600">—</span>}</span>
    </div>
  );
  return (
    <div>
      <button onClick={onBack} className="text-xs text-zinc-400 hover:text-white mb-3 inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to inbox
      </button>
      {warnings.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-950/50 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-300 mb-1">
            <AlertTriangle className="w-4 h-4" /> {warnings.length} thing{warnings.length === 1 ? "" : "s"} to double-check
          </div>
          <ul className="list-disc pl-5 text-xs text-amber-200 space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Landlord</div>
          {f.landlord_unknown ? (
            <div className="text-xs italic text-zinc-500">Tenant marked "I don't know" — fill in yourself.</div>
          ) : (
            <>
              {row("Company", f.company_name)}
              {row("Landlord", f.landlord_name)}
              {row("Phone", f.landlord_phone)}
              {row("Email", f.landlord_email)}
            </>
          )}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Property</div>
          {row("Address", `${f.property_address}${f.property_unit ? ` #${f.property_unit}` : ""}`)}
          {row("City / State", `${f.property_city}, ${f.property_state}`)}
          {row("ZIP", f.property_zip)}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Tenant</div>
          {row("Name(s)", f.tenant_names)}
          {row("Phone", f.tenant_phone)}
          {row("Email", f.tenant_email)}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Rent & Terms</div>
          {row("Term", f.term_type === "fixed" ? "Fixed" : "Month-to-month")}
          {row("Start / End", `${f.start_date || "—"} → ${f.end_date || "—"}`)}
          {row("Move-in", f.move_in_date)}
          {row("Rent", f.monthly_rent ? `$${Number(f.monthly_rent).toLocaleString()}` : "")}
          {row("Deposit", f.security_deposit ? `$${Number(f.security_deposit).toLocaleString()}` : "")}
          {row("Late Fee", f.late_fee_amount ? `$${f.late_fee_amount}` : "")}
          {row("Grace Days", f.grace_days)}
        </div>
      </div>
      {(f.tenant_signature_image || f.tenant_initials_image) && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {f.tenant_signature_image && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Signature</div>
              <img src={f.tenant_signature_image} alt="signature" className="bg-white rounded max-h-28 w-full object-contain" />
            </div>
          )}
          {f.tenant_initials_image && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2">Initials</div>
              <img src={f.tenant_initials_image} alt="initials" className="bg-white rounded max-h-20 w-full object-contain" />
            </div>
          )}
        </div>
      )}
      <div className="mt-5 flex gap-2">
        <button
          onClick={onDismiss}
          className="flex-1 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-semibold"
          data-testid="lease-intake-preview-dismiss"
        >
          Dismiss
        </button>
        <button
          onClick={onImport}
          className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1"
          data-testid="lease-intake-preview-import"
        >
          <FileDown className="w-4 h-4" /> Import into Lease
        </button>
      </div>
    </div>
  );
}

